import { Button } from "@component/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "@component/dialog";
import { Input } from "@component/input";
import { getUserReferral } from "@service/referral";
import { getCookie } from "@util/cookie";
import { trackEvent } from "@util/mixpanel";
import { getSubdomainHost } from "@util/name";
import { catchErrorLog } from "@util/sentry";
import { useEffect, useState } from "react";

interface Props {
    open: boolean;
    onClose: (open: boolean) => void;
    jobTitle?: string;
    jobLink: string;
    companySubdomain: string;
}

export default function DialogShareReferral(props: Props) {
    const token = getCookie("authToken");
    const [copied, setCopied] = useState(false);
    const [referralCode, setReferralCode] = useState<string>("");
    const [isLoading, setLoading] = useState<boolean>(true);
    const referrallink = `${getSubdomainHost(props.companySubdomain)}${props.jobLink}?referral=${referralCode}`;

    useEffect(() => {
        if (!token) return
        getUserReferral().then((response) => {
            if (response && response.data.code === 200) {
                setReferralCode(response.data.data.referral_code || "");
            }
        }).catch((error) => {
            catchErrorLog(`Error fetching referral link: ${error}`);
        }).finally(() => {
            setLoading(false);
        });
    }, [props.open])

    const handleCopy = () => {
        trackEvent("job_referral_link_copied_boxout", {
            job_title: props.jobTitle,
        })
        navigator.clipboard.writeText(referrallink).then(() => {
            setCopied(true);
            trackEvent("job_referral_link_copied", {
                job_title: props.jobTitle,
            })
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Dialog open={props.open} onOpenChange={props.onClose}>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle className="text-xl font-semibold">
                        Refer & earn
                    </DialogTitle>
                </DialogHeader>
                {!isLoading && (
                    <div className="space-y-6">
                        <div>
                            <p className="text-sm text-gray-600 mb-4">Share this link via</p>
                            <div className="flex justify-between max-w-md mx-auto">
                                {/* LinkedIn */}
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="rounded-full w-12 h-12 bg-white hover:bg-gray-100"
                                    onClick={() => {
                                        trackEvent("job_referral_link_social_media", {
                                            "platform": "linkedin",
                                        })
                                        window.open(
                                            `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
                                                referrallink
                                            )}&title=${encodeURIComponent(
                                                `Check out this ${props.jobTitle} role at ourteam!`
                                            )}&utm_source=linkedin&utm_medium=social&utm_campaign=referral`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <svg
                                        className="w-5 h-5 text-[#0077B5]"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                                    </svg>
                                    <span className="sr-only">Share on LinkedIn</span>
                                </Button>

                                {/* Twitter/X */}
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="rounded-full w-12 h-12 bg-white hover:bg-gray-100"
                                    onClick={() => {
                                        trackEvent("job_referral_link_social_media", {
                                            "platform": "twitter",
                                        })
                                        window.open(
                                            `https://twitter.com/intent/tweet?url=${encodeURIComponent(
                                                referrallink
                                            )}&text=${encodeURIComponent(
                                                `Check out this ${props.jobTitle} role at ourteam!`
                                            )}&utm_source=x&utm_medium=social&utm_campaign=referral`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <svg
                                        className="w-5 h-5"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                    <span className="sr-only">Share on Twitter</span>
                                </Button>

                                {/* Facebook */}
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="rounded-full w-12 h-12 bg-white hover:bg-gray-100"
                                    onClick={() => {
                                        trackEvent("job_referral_link_social_media", {
                                            "platform": "facebook",
                                        })
                                        window.open(
                                            `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
                                                referrallink
                                            )}&utm_source=facebook&utm_medium=social&utm_campaign=referral`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <svg
                                        className="w-5 h-5 text-[#1877F2]"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className="sr-only">Share on Facebook</span>
                                </Button>

                                {/* WhatsApp */}
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="rounded-full w-12 h-12 bg-white hover:bg-gray-100"
                                    onClick={() => {
                                        trackEvent("job_referral_link_social_media", {
                                            "platform": "whatsapp",
                                        });
                                        window.open(
                                            `https://wa.me/?text=${encodeURIComponent(
                                                `Check out this ${props.jobTitle} role at ourteam! ${referrallink}`
                                            )}&utm_source=facebook&utm_medium=social&utm_campaign=referral`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <svg
                                        className="w-5 h-5 text-[#25D366]"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path
                                            fillRule="evenodd"
                                            d="M20.463 3.488C18.217 1.24 15.231 0 12.05 0 5.495 0 .16 5.334.157 11.892c0 2.096.546 4.142 1.588 5.946L.057 24l6.304-1.654a11.9 11.9 0 005.684 1.448h.005c6.554 0 11.89-5.335 11.893-11.893 0-3.181-1.24-6.167-3.48-8.413zm-8.413 18.306h-.004a9.9 9.9 0 01-5.031-1.378l-.361-.214-3.741.981.999-3.648-.235-.375a9.837 9.837 0 01-1.51-5.26c.002-5.45 4.437-9.884 9.889-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.898 6.994c-.003 5.45-4.437 9.886-9.892 9.886zm5.43-7.403c-.3-.15-1.767-.872-2.04-.971-.273-.099-.472-.148-.67.15-.2.296-.772.97-.947 1.17-.175.2-.349.223-.648.075-.3-.15-1.263-.465-2.403-1.485-.888-.79-1.489-1.768-1.663-2.066-.175-.297-.019-.458.13-.606.135-.133.3-.347.45-.52.15-.174.2-.298.3-.497.099-.198.05-.371-.025-.52-.075-.148-.67-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.175-.008-.374-.01-.573-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.15.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.871.118.57-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.273-.198-.571-.347z"
                                            clipRule="evenodd"
                                        />
                                    </svg>
                                    <span className="sr-only">Share on WhatsApp</span>
                                </Button>

                                {/* Telegram */}
                                <Button
                                    variant="outline"
                                    size="icon"
                                    className="rounded-full w-12 h-12 bg-white hover:bg-gray-100"
                                    onClick={() => {
                                        trackEvent("job_referral_link_social_media", {
                                            "platform": "telegram",
                                        });
                                        window.open(
                                            `https://t.me/share/url?url=${encodeURIComponent(
                                                referrallink
                                            )}&text=${encodeURIComponent(
                                                `Check out this ${props.jobTitle} role at ourteam!`
                                            )}&utm_source=telegram&utm_medium=social&utm_campaign=referral`,
                                            "_blank"
                                        );
                                    }}
                                >
                                    <svg
                                        className="w-5 h-5 text-[#0088cc]"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z" />
                                    </svg>
                                    <span className="sr-only">Share on Telegram</span>
                                </Button>
                            </div>
                        </div>

                        <div>
                            <p className="text-sm text-gray-600 mb-2">Or copy link</p>
                            <div className="flex space-x-2">
                                <div className="relative flex-1">
                                    <Input
                                        value={referrallink}
                                        readOnly
                                        className="text-sm text-gray-500"
                                    />
                                </div>
                                <Button
                                    type="submit"
                                    onClick={handleCopy}
                                    className="flex-shrink-0 bg-[#0C49FF] hover:bg-[#0A3EE0] text-white"
                                >
                                    {copied ? "Copied!" : "Copy"}
                                </Button>
                            </div>
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
